import React from 'react'
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import SearchableDropdown from '../../shared/SearchableDropdown'
import Button from '../../shared/Button'
import { HelmetInfo, HelmetLookupFormData, Organization } from '../../../types/helmetLookup'
import { ConvertibleValidation, SFEchoValidation } from '../../../types/helmetLookup'
import { isSFEchoHelmet, isConvertibleHelmet } from '../../../utils/helmetLookup'

interface TransferConfirmationProps {
  helmetInfo: HelmetInfo
  organizations: Organization[]
  availableTargetOrgs: Organization[]
  availableTargetTeams: any[]
  isLoadingOrgs: boolean
  isLoadingTargetTeams: boolean
  isSubmitting: boolean
  convertibleValidation: ConvertibleValidation | null
  isValidatingConvertible: boolean
  sfEchoValidation: SFEchoValidation | null
  isValidatingSFEcho: boolean
  watchedTargetOrgID: string
  watchedTargetTeamID: string
  errors: FieldErrors<HelmetLookupFormData>
  register: UseFormRegister<HelmetLookupFormData>
  setValue: UseFormSetValue<HelmetLookupFormData>
  watch: UseFormWatch<HelmetLookupFormData>
}

const TransferConfirmation: React.FC<TransferConfirmationProps> = ({
  helmetInfo,
  organizations,
  availableTargetOrgs,
  availableTargetTeams,
  isLoadingOrgs,
  isLoadingTargetTeams,
  isSubmitting,
  convertibleValidation,
  isValidatingConvertible,
  sfEchoValidation,
  isValidatingSFEcho,
  watchedTargetOrgID,
  watchedTargetTeamID,
  errors,
  register,
  setValue,
  watch
}) => {
  const isConvertible = isConvertibleHelmet(helmetInfo)
  const isSFEcho = isSFEchoHelmet(helmetInfo)
  const canTransfer = (!isConvertible || convertibleValidation?.isValid === true) &&
                      (!isSFEcho || sfEchoValidation?.isValid === true)

  return (
    <>
      {/* Target Organization & Team Selection */}
      {(
        <div className="bg-neutral-50 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="target-org-team-heading">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
            <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">3</div>
            <h2 id="target-org-team-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">SELECT TARGET ORGANIZATION & TEAM</h2>
          </div>
          <div className="w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Target Organization Dropdown */}
              <div>
                <label htmlFor="targetOrgID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                  Target Organization
                </label>
                <SearchableDropdown
                  id="targetOrgID"
                  name="targetOrgID"
                  placeholder="Select target organization..."
                  options={availableTargetOrgs.map(org => ({ 
                    value: org.orgID.toString(), 
                    label: org.orgName 
                  }))}
                  value={watch('targetOrgID') || ''}
                  onChange={(value) => {
                    register('targetOrgID', { required: 'Target organization is required' })
                    setValue('targetOrgID', value, { shouldValidate: true })
                  }}
                  disabled={isLoadingOrgs}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg font-medium bg-white border-2 border-gray-300 rounded-md focus:border-riddell-red focus:outline-none transition-colors duration-150"
                />
                {errors.targetOrgID && (
                  <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                    <span className="mr-1">⚠</span>
                    {errors.targetOrgID.message}
                  </p>
                )}
                {isLoadingOrgs && (
                  <div className="mt-3 flex items-center space-x-2 sm:space-x-3 text-riddell-red">
                    <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-riddell-red border-t-transparent"></div>
                    <span className="text-sm font-medium">Loading organizations...</span>
                  </div>
                )}
              </div>
              {/* Target Team Dropdown or Disabled Field */}
              {watchedTargetOrgID && availableTargetTeams.length > 1 && !isLoadingTargetTeams && (
                <div>
                  <label htmlFor="targetTeamID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                    Target Team
                  </label>
                  <SearchableDropdown
                    id="targetTeamID"
                    name="targetTeamID"
                    placeholder="Select target team..."
                    options={availableTargetTeams.map(team => ({ 
                      value: team.teamID.toString(), 
                      label: team.teamName || team.name || `Team ${team.teamID}` 
                    }))}
                    value={watch('targetTeamID') || ''}
                    onChange={(value) => {
                      register('targetTeamID', { required: 'Target team is required' })
                      setValue('targetTeamID', value, { shouldValidate: true })
                    }}
                    disabled={isLoadingTargetTeams}
                    className="w-full px-4 py-3 text-lg font-medium bg-white border-2 border-gray-300 rounded-md focus:border-riddell-red focus:outline-none transition-colors duration-150"
                  />
                  {errors.targetTeamID && (
                    <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                      <span className="mr-1">⚠</span>
                      {errors.targetTeamID.message}
                    </p>
                  )}
                  {isLoadingTargetTeams && (
                    <div className="mt-3 flex items-center space-x-2 sm:space-x-3 text-riddell-red">
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-riddell-red border-t-transparent"></div>
                      <span className="text-sm font-medium">Loading teams...</span>
                    </div>
                  )}
                </div>
              )}
              {watchedTargetOrgID && availableTargetTeams.length === 1 && !isLoadingTargetTeams && (
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                    Target Team (Auto-selected)
                  </label>
                  <div className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg font-medium bg-gray-100 border-2 border-gray-300 rounded-md">
                    {availableTargetTeams[0]?.teamName || availableTargetTeams[0]?.name || 'Loading...'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation and Submit */}

      {/* Convertible Validation Banner */}
      {isConvertible && watchedTargetTeamID && (
        <div className={`p-3 sm:p-4 lg:p-6 rounded-lg w-full border-l-4 ${
          isValidatingConvertible
            ? 'bg-neutral-50 border-neutral-300'
            : convertibleValidation?.isValid
              ? 'bg-success/10 border-success'
              : 'bg-error/10 border-error'
        }`} role={convertibleValidation?.isValid ? 'status' : 'alert'}>
          {isValidatingConvertible ? (
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-riddell-red border-t-transparent flex-shrink-0"></div>
              <span className="text-sm font-medium text-riddell-black">Validating convertible helmet requirements...</span>
            </div>
          ) : convertibleValidation ? (
            <div className="flex items-start">
              <div className={`flex-shrink-0 ${convertibleValidation.isValid ? 'text-success' : 'text-error'}`}>
                {convertibleValidation.isValid ? (
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm sm:text-base font-bold uppercase tracking-wide ${convertibleValidation.isValid ? 'text-success' : 'text-error'}`}>
                  {convertibleValidation.isValid ? 'Convertible Helmet Validated' : 'Convertible Helmet Requirement Not Met'}
                </p>
                {convertibleValidation.isValid && convertibleValidation.requiredPlayer && (
                  <p className="text-sm sm:text-base text-riddell-black mt-1">
                    This helmet will be assigned to player <span className="font-bold">{convertibleValidation.requiredPlayer.playerName}</span>.
                  </p>
                )}
                {!convertibleValidation.isValid && convertibleValidation.errorMessage && (
                  <p className="text-sm sm:text-base text-riddell-black mt-1">
                    {convertibleValidation.errorMessage}
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* sf-echo Validation Banner */}
      {isSFEcho && watchedTargetTeamID && (
        <div className={`p-3 sm:p-4 lg:p-6 rounded-lg w-full border-l-4 ${
          isValidatingSFEcho
            ? 'bg-neutral-50 border-neutral-300'
            : sfEchoValidation?.isValid
              ? 'bg-success/10 border-success'
              : 'bg-error/10 border-error'
        }`} role={sfEchoValidation?.isValid ? 'status' : 'alert'}>
          {isValidatingSFEcho ? (
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-riddell-red border-t-transparent flex-shrink-0"></div>
              <span className="text-sm font-medium text-riddell-black">Validating SF Echo helmet requirements...</span>
            </div>
          ) : sfEchoValidation ? (
            <div className="flex items-start">
              <div className={`flex-shrink-0 ${sfEchoValidation.isValid ? 'text-success' : 'text-error'}`}>
                {sfEchoValidation.isValid ? (
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm sm:text-base font-bold uppercase tracking-wide ${sfEchoValidation.isValid ? 'text-success' : 'text-error'}`}>
                  {sfEchoValidation.isValid ? 'SF Echo Helmet Validated' : 'SF Echo Helmet Requirement Not Met'}
                </p>
                {sfEchoValidation.isValid && sfEchoValidation.requiredPlayer && (
                  <p className="text-sm sm:text-base text-riddell-black mt-1">
                    This helmet will be assigned to player <span className="font-bold">{sfEchoValidation.requiredPlayer.playerName}</span>.
                  </p>
                )}
                {!sfEchoValidation.isValid && sfEchoValidation.errorMessage && (
                  <p className="text-sm sm:text-base text-riddell-black mt-1">
                    {sfEchoValidation.errorMessage}
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Confirmation and Submit */}
      {canTransfer && watchedTargetOrgID && (availableTargetTeams.length === 1 || (availableTargetTeams.length > 1 && watchedTargetTeamID)) && !isValidatingConvertible && !isValidatingSFEcho && (
        <div className="bg-riddell-red/10 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4 lg:mb-6">
            <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">4</div>
            <h2 className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">CONFIRM TRANSFER</h2>
          </div>
          
          <div className="mb-3 sm:mb-4 lg:mb-6 w-full">
            <label className="flex items-start space-x-2 sm:space-x-3 cursor-pointer">
              <input
                {...register('confirmTransfer', { required: 'You must confirm the transfer' })}
                type="checkbox"
                className="mt-0.5 sm:mt-1 h-4 w-4 sm:h-5 sm:w-5 text-riddell-red bg-white border-2 border-gray-300 rounded focus:ring-2 focus:ring-riddell-red focus:ring-offset-0 transition-all duration-150 flex-shrink-0"
              />
              <span className="text-xs sm:text-sm lg:text-base text-riddell-black leading-relaxed">
                I confirm that I want to transfer helmet <strong className="text-riddell-red">{helmetInfo.helmetRFIDLabel || helmetInfo.rfidBarcode || helmetInfo.helmetId}</strong> from{' '}
                <strong className="text-riddell-black break-words">{helmetInfo.sourceOrgName}</strong> to{' '}
                <strong className="text-riddell-black break-words">{organizations.find(org => org.orgID === parseInt(watchedTargetOrgID))?.orgName || 'the selected target organization'}</strong>
                {availableTargetTeams.length > 0 && (
                  <span>, team <strong className="text-riddell-red">{availableTargetTeams.find(team => team.teamID.toString() === watchedTargetTeamID)?.teamName || availableTargetTeams[0]?.teamName}</strong></span>
                )}.
                {helmetInfo.currentPlayer && (
                  <span className="block mt-2 text-warning font-medium">
                    ⚠ This will unassign the helmet from {helmetInfo.currentPlayer.playerName}
                  </span>
                )}
              </span>
            </label>
            {errors.confirmTransfer && (
              <p className="mt-2 sm:mt-3 text-sm text-error font-medium flex items-center" role="alert">
                <span className="mr-1">⚠</span>
                {errors.confirmTransfer.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            className="w-full py-3 sm:py-4 text-base sm:text-lg font-bold uppercase tracking-wide bg-riddell-red text-white border-2 border-riddell-red rounded-md hover:bg-riddell-red-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent"></div>
                <span>TRANSFERRING...</span>
              </div>
            ) : (
              'TRANSFER HELMET'
            )}
          </Button>
        </div>
      )}
    </>
  )
}

export default TransferConfirmation
